import { parseHtml, userNameRule  } from '@healthhub/react-ui';
import type { Rule, RuleObject, RuleRender } from 'antd/es/form';
import DOMPurify from 'dompurify';

import { APPOINTMENT_REQUEST_FORM_WITHOUT_TYPE_PATH } from '@shared/constants/paths';
import type { Validation } from '@shared/types/form.types';
import { getValidationMessage } from '@shared/utils/form';
import { validateHandPhoneNumber } from '@shared/utils/validator';

import { RequestFormType } from '@features/Appointment/AppointmentMakeNew/types/makeNew';
import { NUMBER_REGEX } from '@features/Appointment/constants';
import { checkHtmlContainsString, formatRequestForm } from '@features/Appointment/utils';

type RuleCreator = ({ message, required }: { message?: string; required?: boolean }) => Rule;

interface ValidateMessageProps {
  message?: string;
  onNavigateRequestForm?: () => void;
}

export const displayValidateMessage = ({ message = '', onNavigateRequestForm }: ValidateMessageProps) => {
  const onClickMessage = (e: React.MouseEvent<HTMLButtonElement>) => {
    const target = e.target as Element;
    const targetHref = target?.getAttribute('href');

    if (!targetHref) {
      e.preventDefault();
      return;
    }

    if (targetHref.startsWith('tel:') || targetHref.startsWith('mailto:')) {
      return;
    }

    if (targetHref.includes(APPOINTMENT_REQUEST_FORM_WITHOUT_TYPE_PATH)) {
      e.preventDefault();
      onNavigateRequestForm?.();
      return;
    }
    e.preventDefault();
  };

  if (message) {
    return checkHtmlContainsString(message) ? (
      <button
        className="text-start text-xs text-error-color [&_a]:text-theme-600"
        onClick={onClickMessage}
        type="button"
      >
        {parseHtml(DOMPurify.sanitize(formatRequestForm({ formType: RequestFormType.Make, responseString: message })))}
      </button>
    ) : (
      message
    );
  }

  return <span />;
};

export const validationMessageRule =
  ({
    onClickValidateMessage,
    validation,
  }: {
    onClickValidateMessage?: () => void;
    validation?: Validation[];
  }): RuleRender =>
  (): RuleObject => ({
    validator(_, value) {
      const validationMessage = getValidationMessage({
        validations: validation,
        value,
      });
      if (validationMessage) {
        return Promise.reject(new Error(validationMessage));
      }
      return Promise.resolve();
    },
    message: displayValidateMessage({
      message: validation?.[0]?.Message || '',
      onNavigateRequestForm: onClickValidateMessage,
    }),
  });

export const requiredRule: RuleCreator = ({ message = 'Required', required = true }) => ({
  message: message ? displayValidateMessage({ message }) : undefined,
  required,
});

export const nameRule = () =>
  userNameRule('Please provide a valid name.', (message) => displayValidateMessage({ message }));

export const handPhoneNumberRule = (message = ''): Rule => ({
  validator(_rule, value) {
    if (!validateHandPhoneNumber(String(value)) && !!value) {
      return Promise.reject(new Error(message));
    }
    return Promise.resolve();
  },
  message: displayValidateMessage({ message }),
});

export const onlyNumberRule = (message = ''): Rule => ({
  validator(_rule, value) {
    if (!value) {
      return Promise.resolve();
    }
    const regEx = new RegExp(NUMBER_REGEX);

    return regEx.test(value) ? Promise.resolve() : Promise.reject(new Error(message));
  },
  message: displayValidateMessage({ message }),
});
